import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PencilIcon, MapPinIcon, BriefcaseIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { composeLocation, parseLocation } from '../../utils/location';
import { CURRICULUM_OPTIONS } from '../../constants/curriculum';
import { locationApi } from '../../services/location.service';
import api from '../../services/api';
import { isNativeMobileRuntime, pickNativeProfileImage } from '../../services/native-mobile.service';
import SubjectSelector from '../SubjectSelector';

interface TeacherProfileFormProps {
  onUpdate?: () => void;
  profileData?: Partial<TeacherProfileData> | null;
  profileLoading?: boolean;
  showCertificationSection?: boolean;
  startInEditMode?: boolean;
}

interface TeacherProfileData {
  avatar?: string;
  location: string;
  experience: string;
  subjects: string[];
  curricula?: string[];
  bio?: string;
  availabilitySlots?: string[];
  upiId?: string;
  upiNumber?: string;
  teachingCourseCertificate?: string;
  aadhaarMasked?: string;
  aadhaarKycStatus?: 'NOT_STARTED' | 'OTP_SENT' | 'VERIFIED' | 'FAILED';
  aadhaarKycUpdatedAt?: string;
  aadhaarKycVerifiedAt?: string;
  qualifications?: Array<{
    level: 'SCHOOL' | 'INTERMEDIATE' | 'DIPLOMA' | 'UG' | 'PG' | 'DOCTORATE' | 'CERTIFICATION' | 'OTHER';
    course: string;
    specialization?: string;
    institute?: string;
    yearOfCompletion?: string;
  }>;
  profileCompletionPercentage?: number;
  profileComplete?: boolean;
  certificationPassed?: boolean;
  leadsEnabled?: boolean;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface SubjectMeta {
  _id?: string;
  id?: string;
  name?: string;
  displayName?: string;
  class?: string;
  stream?: string;
  exam?: string;
  activityType?: string;
}

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const QUALIFICATION_LEVELS = ['SCHOOL', 'INTERMEDIATE', 'DIPLOMA', 'UG', 'PG', 'DOCTORATE', 'CERTIFICATION', 'OTHER'] as const;
const TIME_OPTIONS = [
  '06:00 AM - 07:00 AM',
  '07:00 AM - 08:00 AM',
  '08:00 AM - 09:00 AM',
  '09:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 01:00 PM',
  '01:00 PM - 02:00 PM',
  '02:00 PM - 03:00 PM',
  '03:00 PM - 04:00 PM',
  '04:00 PM - 05:00 PM',
  '05:00 PM - 06:00 PM',
  '06:00 PM - 07:00 PM',
  '07:00 PM - 08:00 PM',
  '08:00 PM - 09:00 PM',
  '09:00 PM - 10:00 PM',
];

const formatAvailabilitySlot = (slot: string) => {
  if (slot.startsWith('DAILY|')) {
    return `Daily - ${slot.split('|')[1]}`;
  }
  const parts = slot.split('|');
  if (parts.length === 2) {
    return `${parts[0]} - ${parts[1]}`;
  }
  return slot;
};

const AVATAR_PREVIEW_SIZE = 96;
const AVATAR_OUTPUT_SIZE = 256;
const DEFAULT_AVATAR_DATA_URI =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect width='100%25' height='100%25' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='54%25' dominant-baseline='middle' text-anchor='middle' fill='%236b7280' font-family='Arial' font-size='22'%3EA%3C/text%3E%3C/svg%3E";

const TeacherProfileForm: React.FC<TeacherProfileFormProps> = ({
  onUpdate,
  profileData,
  profileLoading = false,
  showCertificationSection = false,
  startInEditMode = false,
}) => {
  const { user, updateTeacherProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [isLoading, setIsLoading] = useState(false);
  const [availabilityType, setAvailabilityType] = useState<'DAILY' | 'WEEKLY'>('DAILY');
  const [selectedDay, setSelectedDay] = useState(DAY_OPTIONS[0]);
  const [selectedTime, setSelectedTime] = useState(TIME_OPTIONS[13]);
  const [stateName, setStateName] = useState('');
  const [district, setDistrict] = useState('');
  const [mandal, setMandal] = useState('');
  const [village, setVillage] = useState('');
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [mandalOptions, setMandalOptions] = useState<string[]>([]);
  const [villageOptions, setVillageOptions] = useState<string[]>([]);
  const [subjectLabelMap, setSubjectLabelMap] = useState<Record<string, string>>({});
  const [certificateFileName, setCertificateFileName] = useState('');
  const [avatarFileName, setAvatarFileName] = useState('');
  const [isAvatarReading, setIsAvatarReading] = useState(false);
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [avatarOffset, setAvatarOffset] = useState({ x: 0, y: 0 });
  const [avatarImageSize, setAvatarImageSize] = useState({ width: AVATAR_PREVIEW_SIZE, height: AVATAR_PREVIEW_SIZE });
  const dragStartRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [aadhaarName, setAadhaarName] = useState('');
  const [aadhaarDob, setAadhaarDob] = useState('');
  const [aadhaarGender, setAadhaarGender] = useState<'M' | 'F' | 'T' | ''>('');
  const [aadhaarMobile, setAadhaarMobile] = useState('');
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);
  const [aadhaarError, setAadhaarError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TeacherProfileData>({
    avatar: '',
    location: '',
    experience: '',
    subjects: [],
    curricula: [],
    bio: '',
    availabilitySlots: [],
    upiId: '',
    upiNumber: '',
    teachingCourseCertificate: '',
    qualifications: [],
    aadhaarMasked: '',
    aadhaarKycStatus: 'NOT_STARTED',
  });

  useEffect(() => {
    if (startInEditMode) {
      setIsEditing(true);
    }
  }, [startInEditMode]);

  useEffect(() => {
    if (profileData) {
      const parsedLocation = parseLocation(profileData.location || '');
      setStateName(parsedLocation.state);
      setDistrict(parsedLocation.district);
      setMandal(parsedLocation.mandal);
      setVillage(parsedLocation.village);
      if (profileData.aadhaarKycStatus) {
        setAadhaarOtpSent(profileData.aadhaarKycStatus === 'OTP_SENT');
      }
      setFormData((prev) => ({
        ...prev,
        ...profileData,
        avatar: profileData?.avatar || user?.avatar || '',
        subjects: profileData?.subjects || [],
        curricula: profileData?.curricula || [],
        availabilitySlots: profileData?.availabilitySlots || [],
      }));
      if (!aadhaarMobile && user?.phone) {
        setAadhaarMobile(user.phone);
      }
      return;
    }

    if (user?.teacherProfile) {
      const parsedLocation = parseLocation(user.teacherProfile.location || '');
      setStateName(parsedLocation.state);
      setDistrict(parsedLocation.district);
      setMandal(parsedLocation.mandal);
      setVillage(parsedLocation.village);
      setFormData((prev) => ({
        ...prev,
        ...user.teacherProfile,
        avatar: user.teacherProfile?.avatar || user?.avatar || '',
        subjects: user.teacherProfile?.subjects || [],
        curricula: user.teacherProfile?.curricula || [],
        availabilitySlots: user.teacherProfile?.availabilitySlots || [],
      }));
      if (!aadhaarMobile && user?.phone) {
        setAadhaarMobile(user.phone);
      }
    }
  }, [profileData, user, aadhaarMobile]);

  useEffect(() => {
    const nextLocation = composeLocation(stateName, district, mandal, village);
    if (!nextLocation) return;
    setFormData((prev) => ({
      ...prev,
      location: nextLocation,
    }));
  }, [stateName, district, mandal, village]);

  useEffect(() => {
    const loadStates = async () => {
      setStateOptions(await locationApi.getStates());
    };
    void loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!stateName) {
        setDistrictOptions([]);
        return;
      }
      setDistrictOptions(await locationApi.getDistricts(stateName));
    };
    void loadDistricts();
  }, [stateName]);

  useEffect(() => {
    const loadMandals = async () => {
      if (!stateName || !district) {
        setMandalOptions([]);
        return;
      }
      setMandalOptions(await locationApi.getMandals(stateName, district));
    };
    void loadMandals();
  }, [district, stateName]);

  useEffect(() => {
    const loadVillages = async () => {
      if (!stateName || !district || !mandal) {
        setVillageOptions([]);
        return;
      }
      setVillageOptions(await locationApi.getVillages(stateName, district, mandal));
    };
    void loadVillages();
  }, [district, mandal, stateName]);

  useEffect(() => {
    const buildSubjectLabel = (subject: SubjectMeta) => {
      const primary = subject.displayName || subject.name || '';
      const details = [subject.class, subject.stream, subject.exam, subject.activityType]
        .filter((item) => Boolean(item && String(item).trim()))
        .join(' | ');
      return details ? `${primary} (${details})` : primary;
    };

    const loadSubjects = async () => {
      try {
        const response = await api.get('/subjects/hierarchy');
        const hierarchy = response.data;
        const nextMap: Record<string, string> = {};

        Object.values(hierarchy || {}).forEach((categories: any) => {
          Object.values(categories || {}).forEach((group: any) => {
            Object.values(group || {}).forEach((items: any) => {
              (Array.isArray(items) ? items : []).forEach((subject: SubjectMeta) => {
                const subjectId = String(subject?._id || subject?.id || '').trim();
                if (!subjectId) return;
                const label = buildSubjectLabel(subject);
                if (!label) return;
                nextMap[subjectId] = label;
              });
            });
          });
        });

        setSubjectLabelMap(nextMap);
      } catch (error) {
        console.error('Failed to load subject labels:', error);
        setSubjectLabelMap({});
      }
    };

    void loadSubjects();
  }, []);

  const completionChecks = useMemo(
    () => [
      Boolean(formData.location?.trim()),
      Boolean(formData.experience?.trim()),
      Boolean(formData.bio?.trim()),
      Boolean((formData.subjects || []).length),
      Boolean((formData.curricula || []).length),
      Boolean((formData.availabilitySlots || []).length),
      Boolean(formData.upiId?.trim()),
      Boolean(formData.upiNumber?.trim()),
      Boolean(formData.teachingCourseCertificate?.trim()),
      Boolean((formData.qualifications || []).some((entry) => entry.level && entry.course)),
    ],
    [formData],
  );
  const completedFields = completionChecks.filter(Boolean).length;
  const totalFields = completionChecks.length;
  const completionPercentage = Math.round((completedFields / totalFields) * 100);
  const certificationPassed = Boolean(profileData?.certificationPassed ?? formData.certificationPassed);
  const approvalStatus = profileData?.status ?? formData.status;
  const showCertificationCard = showCertificationSection || !certificationPassed || approvalStatus !== 'APPROVED';

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  const avatarScaleBase = useMemo(
    () => Math.max(AVATAR_PREVIEW_SIZE / avatarImageSize.width, AVATAR_PREVIEW_SIZE / avatarImageSize.height),
    [avatarImageSize.height, avatarImageSize.width],
  );
  const avatarRenderWidth = avatarImageSize.width * avatarScaleBase * avatarZoom;
  const avatarRenderHeight = avatarImageSize.height * avatarScaleBase * avatarZoom;
  const maxAvatarOffsetX = Math.max(0, (avatarRenderWidth - AVATAR_PREVIEW_SIZE) / 2);
  const maxAvatarOffsetY = Math.max(0, (avatarRenderHeight - AVATAR_PREVIEW_SIZE) / 2);

  useEffect(() => {
    const source = String(formData.avatar || '').trim();
    if (!source) return;
    const image = new Image();
    image.onload = () => {
      setAvatarImageSize({
        width: image.naturalWidth || AVATAR_PREVIEW_SIZE,
        height: image.naturalHeight || AVATAR_PREVIEW_SIZE,
      });
    };
    image.src = source;
  }, [formData.avatar]);

  useEffect(() => {
    setAvatarOffset((prev) => ({
      x: clamp(prev.x, -maxAvatarOffsetX, maxAvatarOffsetX),
      y: clamp(prev.y, -maxAvatarOffsetY, maxAvatarOffsetY),
    }));
  }, [maxAvatarOffsetX, maxAvatarOffsetY]);

  const handleAvatarPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isEditing) return;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: avatarOffset.x,
      offsetY: avatarOffset.y,
    };
  };

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const drag = dragStartRef.current;
      if (!drag) return;
      const deltaX = e.clientX - drag.x;
      const deltaY = e.clientY - drag.y;
      setAvatarOffset({
        x: clamp(drag.offsetX + deltaX, -maxAvatarOffsetX, maxAvatarOffsetX),
        y: clamp(drag.offsetY + deltaY, -maxAvatarOffsetY, maxAvatarOffsetY),
      });
    };
    const onPointerUp = () => {
      dragStartRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [maxAvatarOffsetX, maxAvatarOffsetY]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleCertificateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertificateFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({
        ...prev,
        teachingCourseCertificate: String(reader.result || ''),
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSizeBytes = 15 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error('Profile image must be 15MB or less.');
      return;
    }
    setAvatarFileName(file.name);
    setAvatarZoom(1);
    setAvatarOffset({ x: 0, y: 0 });
    setIsAvatarReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const avatarValue = String(reader.result || '');
      console.log('[Avatar Debug] upload read avatar length:', avatarValue.length);
      setFormData((prev) => ({
        ...prev,
        avatar: avatarValue,
      }));
      setIsAvatarReading(false);
    };
    reader.onerror = () => {
      setIsAvatarReading(false);
      toast.error('Failed to read image file. Please try another image.');
    };
    reader.readAsDataURL(file);
  };

  const handleNativeAvatarPick = async () => {
    try {
      setAvatarFileName('Camera/gallery image');
      setAvatarZoom(1);
      setAvatarOffset({ x: 0, y: 0 });
      setIsAvatarReading(true);
      const avatarValue = await pickNativeProfileImage();
      if (!avatarValue) {
        toast.info('Native image picker is available inside the Android app.');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        avatar: avatarValue,
      }));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to capture image. Please try again.');
    } finally {
      setIsAvatarReading(false);
    }
  };

  const resolveApiErrorMessage = (error: unknown, fallback: string) => {
    if (!error || typeof error !== 'object') return fallback;
    const response = (error as { response?: { data?: any } }).response;
    const message = response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (message && typeof message === 'object' && typeof message.message === 'string') {
      return message.message;
    }
    if (Array.isArray(message) && message.length > 0) return String(message[0]);
    return fallback;
  };

  const handleRequestAadhaarOtp = async () => {
    setAadhaarError(null);
    const aadhaar = aadhaarNumber.replace(/\s+/g, '');
    if (!/^\d{12}$/.test(aadhaar)) {
      setAadhaarError('Enter a valid 12-digit Aadhaar number.');
      return;
    }
    if (!aadhaarName.trim() || !aadhaarDob.trim() || !aadhaarGender || !aadhaarMobile.trim()) {
      setAadhaarError('Name, DOB, gender, and mobile are required.');
      return;
    }

    try {
      setAadhaarLoading(true);
      const response = await api.post('/teachers/profile/aadhaar/otp', {
        aadhaar,
        name: aadhaarName.trim(),
        dob: aadhaarDob.trim(),
        gender: aadhaarGender,
        mobile: aadhaarMobile.trim(),
        consent: 'Y',
      });
      const data = response.data || {};
      if (data.otp) {
        setAadhaarOtp(String(data.otp));
        toast.info(`Mock OTP: ${data.otp}`);
      }
      setFormData((prev) => ({
        ...prev,
        aadhaarMasked: data.aadhaarMasked || prev.aadhaarMasked,
        aadhaarKycStatus: data.status || 'OTP_SENT',
        aadhaarKycUpdatedAt: new Date().toISOString(),
      }));
      setAadhaarOtpSent(true);
      toast.success('OTP sent to your Aadhaar linked mobile.');
    } catch (error) {
      const message = resolveApiErrorMessage(error, 'Failed to request Aadhaar OTP.');
      setAadhaarError(message);
      toast.error(message);
    } finally {
      setAadhaarLoading(false);
    }
  };

  const handleVerifyAadhaarOtp = async () => {
    setAadhaarError(null);
    if (!aadhaarMobile.trim() || !aadhaarOtp.trim()) {
      setAadhaarError('Mobile and OTP are required.');
      return;
    }

    try {
      setAadhaarLoading(true);
      const response = await api.post('/teachers/profile/aadhaar/verify', {
        mobile: aadhaarMobile.trim(),
        otp: aadhaarOtp.trim(),
      });
      const data = response.data || {};
      setFormData((prev) => ({
        ...prev,
        aadhaarMasked: data.aadhaarMasked || prev.aadhaarMasked,
        aadhaarKycStatus: data.status || 'VERIFIED',
        aadhaarKycVerifiedAt: data.verifiedAt || new Date().toISOString(),
      }));
      setAadhaarOtpSent(false);
      setAadhaarOtp('');
      setAadhaarNumber('');
      toast.success('Aadhaar verified successfully.');
    } catch (error) {
      const message = resolveApiErrorMessage(error, 'Failed to verify Aadhaar OTP.');
      setAadhaarError(message);
      toast.error(message);
    } finally {
      setAadhaarLoading(false);
    }
  };

  const buildAdjustedAvatar = async (): Promise<string | undefined> => {
    const source = String(formData.avatar || '').trim();
    if (!source) return undefined;

    try {
      const image = new Image();
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Unable to load avatar image'));
        image.src = source;
      });

      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_OUTPUT_SIZE;
      canvas.height = AVATAR_OUTPUT_SIZE;
      const context = canvas.getContext('2d');
      if (!context) return source;

      const outputScale = AVATAR_OUTPUT_SIZE / AVATAR_PREVIEW_SIZE;
      const drawWidth = avatarRenderWidth * outputScale;
      const drawHeight = avatarRenderHeight * outputScale;
      const drawX = ((AVATAR_OUTPUT_SIZE - drawWidth) / 2) + (avatarOffset.x * outputScale);
      const drawY = ((AVATAR_OUTPUT_SIZE - drawHeight) / 2) + (avatarOffset.y * outputScale);

      context.clearRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
      context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      return canvas.toDataURL('image/jpeg', 0.82);
    } catch (error) {
      console.warn('Failed to apply avatar framing adjustments:', error);
      return source;
    }
  };

  const handleAddAvailabilitySlot = () => {
    const slot = availabilityType === 'DAILY' ? `DAILY|${selectedTime}` : `${selectedDay}|${selectedTime}`;
    const existing = formData.availabilitySlots || [];
    if (existing.includes(slot)) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      availabilitySlots: [...existing, slot],
    }));
  };

  const handleRemoveAvailabilitySlot = (slotToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      availabilitySlots: (prev.availabilitySlots || []).filter((slot) => slot !== slotToRemove),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    try {
      setIsLoading(true);
      if (isAvatarReading) {
        toast.info('Image is still processing. Please wait a moment and save again.');
        return;
      }
      const composedLocation = composeLocation(stateName, district, mandal, village);
      const adjustedAvatar = await buildAdjustedAvatar();
      const payload = {
        avatar: adjustedAvatar || undefined,
        location: composedLocation || formData.location || '',
        experience: formData.experience || '',
        subjects: formData.subjects || [],
        curricula: formData.curricula || [],
        bio: formData.bio || '',
        availabilitySlots: formData.availabilitySlots || [],
        upiId: (formData.upiId || '').trim() || undefined,
        upiNumber: (formData.upiNumber || '').trim() || undefined,
        teachingCourseCertificate: (formData.teachingCourseCertificate || '').trim() || undefined,
        qualifications: (formData.qualifications || []).filter(
          (entry) => String(entry.course || '').trim() && String(entry.level || '').trim(),
        ),
      };
      const avatarLength = String(payload.avatar || '').length;
      console.log('[Avatar Debug] submit payload avatar length:', avatarLength);
      if (payload.avatar) {
        toast.info(`Avatar debug: sending ${Math.round(avatarLength / 1024)} KB`);
      }
      await updateTeacherProfile(payload);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addQualification = () => {
    setFormData((prev) => ({
      ...prev,
      qualifications: [
        ...(prev.qualifications || []),
        { level: 'UG', course: '', specialization: '', institute: '', yearOfCompletion: '' },
      ],
    }));
  };
  const updateQualification = (index: number, key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: (prev.qualifications || []).map((entry, idx) =>
        idx === index ? { ...entry, [key]: value } : entry,
      ),
    }));
  };
  const removeQualification = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: (prev.qualifications || []).filter((_, idx) => idx !== index),
    }));
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.45)]">
        <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_28%),linear-gradient(135deg,_#020617_0%,_#0f172a_56%,_#1d4ed8_100%)] px-6 py-6 text-white sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-100">Teacher Identity</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">Teacher Profile</h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-200">
                Build a credible public profile with your subjects, location, credentials, payments, and availability.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              {isEditing ? 'Cancel editing' : 'Edit profile'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-5 sm:p-6">
          {profileLoading && (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
              Loading profile from server...
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]">
            <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50/70 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Identity</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">Profile Image</p>
                  <p className="mt-1 text-sm text-slate-500">Use a clean portrait so students and admins can identify you quickly.</p>
                </div>
                <span className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                  {isEditing ? 'Editable' : 'Preview'}
                </span>
              </div>
              <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div
                onPointerDown={handleAvatarPointerDown}
                className={`relative h-24 w-24 overflow-hidden rounded-full border border-slate-200 bg-slate-100 touch-none shadow-inner ${
                  isEditing ? 'cursor-grab active:cursor-grabbing' : ''
                }`}
              >
                <img
                  src={formData.avatar || user?.avatar || DEFAULT_AVATAR_DATA_URI}
                  alt="Teacher profile"
                  className="pointer-events-none absolute select-none"
                  style={{
                    width: `${avatarRenderWidth}px`,
                    height: `${avatarRenderHeight}px`,
                    left: `${(AVATAR_PREVIEW_SIZE - avatarRenderWidth) / 2 + avatarOffset.x}px`,
                    top: `${(AVATAR_PREVIEW_SIZE - avatarRenderHeight) / 2 + avatarOffset.y}px`,
                  }}
                />
              </div>
              {isEditing ? (
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/jpg"
                    onChange={handleAvatarUpload}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                  />
                  {isNativeMobileRuntime() ? (
                    <button
                      type="button"
                      onClick={handleNativeAvatarPick}
                      className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Use camera or gallery
                    </button>
                  ) : null}
                  {avatarFileName ? (
                    <p className="text-xs text-slate-600">Uploaded: {avatarFileName}</p>
                  ) : null}
                  {isAvatarReading ? <p className="text-xs text-sky-600">Processing image...</p> : null}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-600">Zoom</label>
                    <input
                      type="range"
                      min="1"
                      max="2.5"
                      step="0.01"
                      value={avatarZoom}
                      onChange={(e) => setAvatarZoom(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Drag the image inside the circle to adjust framing.</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Upload a clear profile picture.</p>
              )}
            </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-[1.75rem] border border-indigo-200 bg-indigo-50 p-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-indigo-900">Profile completion</span>
                  <span className="font-semibold text-indigo-900">
                    {completionPercentage}% ({completedFields}/{totalFields})
                  </span>
                </div>
                <div className="mt-3 h-2.5 w-full rounded-full bg-indigo-100">
                  <div
                    className="h-2.5 rounded-full bg-indigo-600 transition-all"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                <p className="mt-3 text-xs text-indigo-800">
                  Complete all fields including qualification details to reach 100%.
                </p>
              </div>

              {showCertificationCard ? (
                <div className="rounded-[1.75rem] border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Teacher Certification Exam</p>
                      <p className="mt-1 text-xs text-amber-800">
                        Pass certification and wait for admin approval to enable Get Leads.
                      </p>
                    </div>
                    {certificationPassed ? (
                      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                        Passed
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                        Not Passed
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to="/teacher/certification-exam"
                      className="rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                    >
                      Attempt Now
                    </Link>
                    <Link
                      to="/teacher/dashboard?tab=sessions"
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Attempt Later
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.4)]">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Teaching Details</p>
              <p className="mt-2 text-lg font-semibold text-slate-950">Core profile information</p>
            </div>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label htmlFor="location" className="block text-sm font-medium text-slate-700">
                <div className="flex items-center">
                  <MapPinIcon className="mr-2 h-5 w-5 text-slate-400" />
                  Location
                </div>
              </label>
              {isEditing ? (
                <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
                  <select
                    value={stateName}
                    onChange={(e) => {
                      setStateName(e.target.value);
                      setDistrict('');
                      setMandal('');
                      setVillage('');
                    }}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  >
                    <option value="">Select state</option>
                    {stateOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <select
                    value={district}
                    onChange={(e) => {
                      setDistrict(e.target.value);
                      setMandal('');
                      setVillage('');
                    }}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  >
                    <option value="">Select district</option>
                    {districtOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <select
                    value={mandal}
                    onChange={(e) => {
                      setMandal(e.target.value);
                      setVillage('');
                    }}
                    disabled={!stateName || !district}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white disabled:bg-slate-100"
                  >
                    <option value="">Select mandal</option>
                    {mandalOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <select
                    value={village}
                    onChange={(e) => setVillage(e.target.value)}
                    disabled={!stateName || !district || !mandal}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white disabled:bg-slate-100"
                  >
                    <option value="">Select village</option>
                    {villageOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-900">{formData.location || 'Not specified'}</p>
              )}
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="experience" className="block text-sm font-medium text-slate-700">
                <div className="flex items-center">
                  <BriefcaseIcon className="mr-2 h-5 w-5 text-slate-400" />
                  Years of Experience
                </div>
              </label>
              {isEditing ? (
                <select
                  name="experience"
                  id="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                >
                  <option value="">Select years of experience</option>
                  <option value="Less than 1 year">Less than 1 year</option>
                  <option value="1-3 years">1-3 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5-10 years">5-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              ) : (
                <p className="mt-2 text-sm text-slate-900">{formData.experience || 'Not specified'}</p>
              )}
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="bio" className="block text-sm font-medium text-slate-700">
                <div className="flex items-center">
                  <BookOpenIcon className="mr-2 h-5 w-5 text-slate-400" />
                  Bio
                </div>
              </label>
              {isEditing ? (
                <textarea
                  name="bio"
                  id="bio"
                  rows={3}
                  value={formData.bio || ''}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              ) : (
                <p className="mt-2 whitespace-pre-line text-sm text-slate-900">{formData.bio || 'No bio provided'}</p>
              )}
            </div>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.4)]">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Capability Stack</p>
              <p className="mt-2 text-lg font-semibold text-slate-950">Subjects, qualifications, and certificate</p>
            </div>
            <div className="sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-slate-700">Subjects</label>
              {isEditing ? (
                <SubjectSelector
                  value={formData.subjects || []}
                  onChange={(value) => {
                    // For now, store the IDs. In a real app, you'd map IDs to display names
                    setFormData(prev => ({ 
                      ...prev, 
                      subjects: Array.isArray(value) ? value : (value ? [value] : [])
                    }));
                  }}
                  mode="multiple"
                  placeholder="Select subjects you teach"
                  className="w-full"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {formData.subjects?.length > 0 ? (
                    formData.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-800"
                      >
                        {subjectLabelMap[subject] || subject}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No subjects selected</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-slate-700">Supported Curricula / Boards</label>
              {isEditing ? (
                <div className="flex flex-wrap gap-2">
                  {CURRICULUM_OPTIONS.map((curriculum) => {
                    const isSelected = (formData.curricula || []).includes(curriculum);
                    return (
                      <button
                        key={curriculum}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            curricula: isSelected
                              ? (prev.curricula || []).filter((item) => item !== curriculum)
                              : [...(prev.curricula || []), curriculum],
                          }))
                        }
                        className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold transition ${
                          isSelected
                            ? 'bg-rose-600 text-white hover:bg-rose-700'
                            : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {curriculum}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(formData.curricula || []).length > 0 ? (
                    (formData.curricula || []).map((curriculum) => (
                      <span
                        key={curriculum}
                        className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800"
                      >
                        {curriculum}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No curricula selected</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 sm:col-span-6">
              <label className="block text-sm font-medium text-slate-700">Qualifications</label>
              {isEditing ? (
                <div className="mt-2 space-y-3">
                  {(formData.qualifications || []).map((entry, index) => (
                    <div key={`qualification-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
                        <select
                          value={entry.level}
                          onChange={(e) => updateQualification(index, 'level', e.target.value)}
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        >
                          {QUALIFICATION_LEVELS.map((level) => (
                            <option key={level} value={level}>
                              {level}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={entry.course || ''}
                          onChange={(e) => updateQualification(index, 'course', e.target.value)}
                          placeholder="Course / Degree"
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        />
                        <input
                          type="text"
                          value={entry.specialization || ''}
                          onChange={(e) => updateQualification(index, 'specialization', e.target.value)}
                          placeholder="Specialization"
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        />
                        <input
                          type="text"
                          value={entry.institute || ''}
                          onChange={(e) => updateQualification(index, 'institute', e.target.value)}
                          placeholder="Institute"
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        />
                        <input
                          type="text"
                          value={entry.yearOfCompletion || ''}
                          onChange={(e) => updateQualification(index, 'yearOfCompletion', e.target.value)}
                          placeholder="Year"
                          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeQualification(index)}
                        className="mt-3 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addQualification}
                    className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Add Qualification
                  </button>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {(formData.qualifications || []).length > 0 ? (
                    (formData.qualifications || []).map((entry, index) => (
                      <div key={`qualification-view-${index}`} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                        <span className="font-semibold">{entry.level}</span>: {entry.course}
                        {entry.specialization ? ` (${entry.specialization})` : ''}
                        {entry.institute ? ` - ${entry.institute}` : ''}
                        {entry.yearOfCompletion ? `, ${entry.yearOfCompletion}` : ''}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No qualifications added</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 sm:col-span-6">
              <label htmlFor="teachingCourseCertificate" className="block text-sm font-medium text-slate-700">
                Teaching Course Certificate
              </label>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="file"
                    id="teachingCourseCertificate"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleCertificateUpload}
                    className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  />
                  {certificateFileName && (
                    <p className="text-xs text-slate-600">Uploaded: {certificateFileName}</p>
                  )}
                  <input
                    type="text"
                    name="teachingCourseCertificate"
                    value={formData.teachingCourseCertificate || ''}
                    onChange={handleChange}
                    placeholder="Or paste certificate URL"
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  />
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-900">
                  {formData.teachingCourseCertificate ? 'Uploaded / Provided' : 'Not provided'}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.4)]">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Compliance And Payments</p>
              <p className="mt-2 text-lg font-semibold text-slate-950">Verification, UPI, and availability</p>
            </div>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label className="block text-sm font-medium text-slate-700">Aadhaar Verification</label>
              <div className="mt-2 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="font-medium text-slate-700">Status:</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    formData.aadhaarKycStatus === 'VERIFIED'
                      ? 'bg-green-100 text-green-700'
                      : formData.aadhaarKycStatus === 'OTP_SENT'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {formData.aadhaarKycStatus || 'NOT_STARTED'}
                  </span>
                  {formData.aadhaarMasked && (
                    <span className="text-slate-600">Aadhaar: {formData.aadhaarMasked}</span>
                  )}
                  {formData.aadhaarKycVerifiedAt && formData.aadhaarKycStatus === 'VERIFIED' && (
                    <span className="text-slate-500">
                      Verified on {new Date(formData.aadhaarKycVerifiedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {isEditing && formData.aadhaarKycStatus !== 'VERIFIED' && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input
                        type="text"
                        value={aadhaarNumber}
                        onChange={(e) => setAadhaarNumber(e.target.value)}
                        placeholder="Aadhaar number"
                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      />
                      <input
                        type="text"
                        value={aadhaarName}
                        onChange={(e) => setAadhaarName(e.target.value)}
                        placeholder="Name as per Aadhaar"
                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      />
                      <input
                        type="date"
                        value={aadhaarDob}
                        onChange={(e) => setAadhaarDob(e.target.value)}
                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      />
                      <select
                        value={aadhaarGender}
                        onChange={(e) => setAadhaarGender(e.target.value as 'M' | 'F' | 'T' | '')}
                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      >
                        <option value="">Select gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="T">Transgender</option>
                      </select>
                      <input
                        type="text"
                        value={aadhaarMobile}
                        onChange={(e) => setAadhaarMobile(e.target.value)}
                        placeholder="Mobile linked to Aadhaar"
                        className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleRequestAadhaarOtp}
                        disabled={aadhaarLoading}
                        className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                      >
                        {aadhaarLoading ? 'Sending OTP...' : 'Send OTP'}
                      </button>
                      {aadhaarOtpSent && (
                        <>
                          <input
                            type="text"
                            value={aadhaarOtp}
                            onChange={(e) => setAadhaarOtp(e.target.value)}
                            placeholder="Enter OTP"
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleVerifyAadhaarOtp}
                            disabled={aadhaarLoading}
                            className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                          >
                            {aadhaarLoading ? 'Verifying...' : 'Verify OTP'}
                          </button>
                        </>
                      )}
                    </div>

                    {aadhaarError && (
                      <p className="text-sm text-rose-600">{aadhaarError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="upiId" className="block text-sm font-medium text-slate-700">
                UPI ID
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="upiId"
                  id="upiId"
                  value={formData.upiId || ''}
                  onChange={handleChange}
                  placeholder="teacher@upi"
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              ) : (
                <p className="mt-2 text-sm text-slate-900">{formData.upiId || 'Not provided'}</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="upiNumber" className="block text-sm font-medium text-slate-700">
                UPI Mobile Number
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="upiNumber"
                  id="upiNumber"
                  value={formData.upiNumber || ''}
                  onChange={handleChange}
                  placeholder="+91XXXXXXXXXX"
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              ) : (
                <p className="mt-2 text-sm text-slate-900">{formData.upiNumber || 'Not provided'}</p>
              )}
            </div>

            <div className="sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-slate-700">Availability Slots</label>
              {isEditing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <select
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value as 'DAILY' | 'WEEKLY')}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
                    >
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                    </select>
                    {availabilityType === 'WEEKLY' && (
                      <select
                        value={selectedDay}
                        onChange={(e) => setSelectedDay(e.target.value)}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
                      >
                        {DAY_OPTIONS.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    )}
                    <select
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm"
                    >
                      {TIME_OPTIONS.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddAvailabilitySlot}
                      className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(formData.availabilitySlots || []).length > 0 ? (
                      (formData.availabilitySlots || []).map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => handleRemoveAvailabilitySlot(slot)}
                          className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-800 hover:bg-indigo-200"
                          title="Click to remove"
                        >
                          {formatAvailabilitySlot(slot)} x
                        </button>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No availability slots added</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(formData.availabilitySlots || []).length > 0 ? (
                    (formData.availabilitySlots || []).map((slot) => (
                      <span
                        key={slot}
                        className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800"
                      >
                        {formatAvailabilitySlot(slot)}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No availability provided</p>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>

          {isEditing && (
            <div className="border-t border-slate-200 pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="rounded-full border border-slate-200 bg-white py-2 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ml-3 inline-flex justify-center rounded-full border border-transparent bg-slate-950 py-2 px-5 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TeacherProfileForm;
